/**
 * Gift calculator panel (CHAP-301) - fundraising surface.
 *
 * Donors give from assets, not income: per-vehicle after-tax math
 * (deduction, capital gains avoided, net cost) computed live and purely
 * client-side via shared/gift-calculator.ts. The tenant's educational-space
 * deductibility ruling (CHAP-231) scales the deduction line when present.
 * The only write is the cabinet+ save-scenario action.
 */
import { useMemo, useState } from 'react';
import {
  computeGiftOutcome,
  GIFT_CALCULATOR_DISCLAIMER,
  GIFT_VEHICLES,
  QCD_MIN_AGE,
  type GiftAssetType,
  type GiftInput,
} from '../../../shared/gift-calculator.js';
import { useGiftScenarios, useSaveGiftScenario, useTaxRuling } from '../../hooks/useGiftScenarios.js';
import { TierGuard } from '../RoleGuard.js';
import { formatCurrencyFull } from '../../lib/formatters.js';

export function GiftCalculator({ slug }: { slug: string }) {
  const { data: rulingData } = useTaxRuling(slug);
  const ruling = rulingData?.ruling ?? null;

  const [assetType, setAssetType] = useState<GiftAssetType>('stock');
  const [amountStr, setAmountStr] = useState('100000');
  const [basisStr, setBasisStr] = useState('');
  const [rateStr, setRateStr] = useState('35');
  const [ageStr, setAgeStr] = useState('');

  const vehicle = GIFT_VEHICLES.find((v) => v.key === assetType) ?? GIFT_VEHICLES[0]!;

  const input: GiftInput = useMemo(
    () => ({
      assetType,
      amount: parseNum(amountStr) ?? 0,
      costBasis: vehicle.usesBasis ? parseNum(basisStr) : null,
      marginalRatePct: parseNum(rateStr) ?? 0,
      age: vehicle.usesAge ? parseNum(ageStr) : null,
    }),
    [assetType, amountStr, basisStr, rateStr, ageStr, vehicle],
  );

  const outcome = useMemo(
    () => computeGiftOutcome(input, ruling ? { percentage: ruling.percentage } : null),
    [input, ruling],
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-w-0">
      <div className="flex items-start justify-between gap-3 flex-wrap min-w-0 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <CalculatorIcon />
          <h3 className="text-navy font-semibold text-sm sm:text-base">Gift Calculator</h3>
        </div>
        {ruling && (
          <span className="text-[11px] bg-graph-blue/10 text-graph-blue px-2 py-1 rounded-full font-semibold flex-shrink-0">
            Educational-space ruling: {ruling.percentage}% deductible
            {ruling.dated ? ` (dated ${ruling.dated})` : ''}
          </span>
        )}
      </div>
      <p className="text-neutral text-xs sm:text-sm mb-4">
        After-tax cost of a gift by asset type. Donors give from assets, not income.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6">
        {/* Inputs */}
        <div className="lg:col-span-2 space-y-3 min-w-0">
          <Field label="Asset type">
            <select
              value={assetType}
              onChange={(e) => setAssetType(e.target.value as GiftAssetType)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy bg-white"
            >
              {GIFT_VEHICLES.map((v) => (
                <option key={v.key} value={v.key}>{v.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Gift amount (fair market value, $)">
            <NumberInput value={amountStr} onChange={setAmountStr} placeholder="100000" min={0} />
          </Field>
          {vehicle.usesBasis && (
            <Field label="Cost basis ($)">
              <NumberInput value={basisStr} onChange={setBasisStr} placeholder="20000" min={0} />
            </Field>
          )}
          <Field label="Marginal income tax rate (%)">
            <NumberInput value={rateStr} onChange={setRateStr} placeholder="35" min={0} max={60} />
          </Field>
          {vehicle.usesAge && (
            <Field label={`Donor age (QCD requires ${QCD_MIN_AGE}+)`}>
              <NumberInput value={ageStr} onChange={setAgeStr} placeholder="72" min={0} max={120} step={0.5} />
            </Field>
          )}
        </div>

        {/* Outputs */}
        <div className="lg:col-span-3 min-w-0">
          {!outcome.eligible ? (
            <div className="h-full flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100 p-6 min-w-0">
              <p className="text-neutral text-sm text-center">{outcome.ineligibleReason}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {outcome.incomeExcluded > 0 ? (
                <OutputRow label="Income excluded (QCD)" value={formatCurrencyFull(outcome.incomeExcluded)} />
              ) : (
                <OutputRow label="Charitable deduction" value={formatCurrencyFull(outcome.deduction)} />
              )}
              {outcome.rulingPercentageApplied != null && (
                <OutputRow
                  label={`After educational-space ruling (${outcome.rulingPercentageApplied}%)`}
                  value={formatCurrencyFull(outcome.deductionAfterRuling)}
                />
              )}
              <OutputRow label="Tax savings" value={formatCurrencyFull(outcome.taxSavings)} />
              <OutputRow
                label={
                  outcome.assumedLtcgRatePct > 0
                    ? `Capital gains avoided (on ${formatCurrencyFull(outcome.appreciationSheltered)} at ${outcome.assumedLtcgRatePct}%)`
                    : 'Capital gains avoided'
                }
                value={formatCurrencyFull(outcome.capitalGainsTaxAvoided)}
              />
              <div className="flex items-baseline justify-between gap-3 bg-orange/5 border border-orange/20 rounded-lg px-3 py-2.5 min-w-0">
                <span className="text-navy text-sm font-semibold min-w-0">Net cost of gift</span>
                <span className="text-orange text-xl sm:text-2xl font-bold whitespace-nowrap flex-shrink-0">
                  {formatCurrencyFull(outcome.netCost)}
                </span>
              </div>
              {outcome.notes.length > 0 && (
                <ul className="text-neutral text-[11px] sm:text-xs space-y-1 pt-1 list-disc pl-4">
                  {outcome.notes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Save scenario - cabinet+ only (per-tenant persistence) */}
      <TierGuard tier="cabinet">
        <SaveScenarioSection slug={slug} input={input} eligible={outcome.eligible} />
      </TierGuard>

      <p className="text-neutral/80 text-[11px] italic border-t border-gray-100 mt-4 pt-3">
        {GIFT_CALCULATOR_DISCLAIMER}
      </p>
    </div>
  );
}

/**
 * Cabinet-only save action + saved-scenario list. Mounted only inside the
 * TierGuard so the cabinet-gated queries never fire for other tiers.
 */
function SaveScenarioSection({
  slug,
  input,
  eligible,
}: {
  slug: string;
  input: GiftInput;
  eligible: boolean;
}) {
  const [name, setName] = useState('');
  const { data } = useGiftScenarios(slug);
  const save = useSaveGiftScenario(slug);
  const scenarios = data?.scenarios ?? [];

  const onSave = () => {
    if (!name.trim() || !eligible) return;
    save.mutate(
      { name: name.trim(), ...input },
      { onSuccess: () => setName('') },
    );
  };

  return (
    <div className="border-t border-gray-100 mt-4 pt-4 min-w-0">
      <div className="flex items-end gap-2 flex-wrap min-w-0">
        <div className="flex-1 min-w-[180px]">
          <Field label="Scenario name">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="e.g. Smith $100K stock ask"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy"
            />
          </Field>
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={!name.trim() || !eligible || save.isPending}
          className="flex-shrink-0 bg-orange text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-40 hover:opacity-90 transition-opacity"
        >
          {save.isPending ? 'Saving…' : 'Save scenario'}
        </button>
      </div>
      {save.isError && (
        <p className="text-red-700 text-xs mt-2">Save failed: {save.error.message}</p>
      )}

      {scenarios.length > 0 && (
        <div className="mt-3 space-y-1.5">
          <p className="text-neutral/70 text-[10px] uppercase tracking-widest font-semibold">Saved scenarios</p>
          {scenarios.map((s) => (
            <div
              key={s.id}
              className="flex items-baseline justify-between gap-3 text-xs bg-gray-50 rounded-lg px-3 py-2 min-w-0"
            >
              <div className="min-w-0">
                <span className="text-navy font-semibold truncate">{s.name}</span>
                <span className="text-neutral ml-2">
                  {GIFT_VEHICLES.find((v) => v.key === s.assetType)?.label ?? s.assetType}
                  {' · '}
                  {formatCurrencyFull(s.inputs.amount)}
                </span>
              </div>
              <span className="text-navy whitespace-nowrap flex-shrink-0">
                net {formatCurrencyFull(s.outputs.netCost)}
                <span className="text-neutral/70 ml-2">{new Date(s.createdAt).toLocaleDateString()}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OutputRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-1.5 min-w-0">
      <span className="text-neutral text-xs sm:text-sm min-w-0">{label}</span>
      <span className="text-navy text-sm sm:text-base font-semibold whitespace-nowrap flex-shrink-0">{value}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="block text-neutral text-xs font-semibold mb-1">{label}</span>
      {children}
    </label>
  );
}

function NumberInput({
  value,
  onChange,
  placeholder,
  min,
  max,
  step,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <input
      type="number"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      min={min}
      max={max}
      step={step}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy"
    />
  );
}

function parseNum(s: string): number | null {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function CalculatorIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0 text-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm1.748-6.75h.007v.008h-.007V9zm-9.75 0h.008v.008H8.25V9zM6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25zM6.75 6.75h10.5v1.5H6.75v-1.5z" />
    </svg>
  );
}
